import type { TestIntent } from '@/types'
import { normalizeIntent } from '@/lib/intent'
import { INTENT_BADGE, INTENT_NEUTRAL } from '@/lib/intentBadge'

/**
 * Inline test-type dropdown (happy-path / negative / edge-case) — the one
 * control both the Tests screen (`TestDetail`) and the proposed-candidate
 * card (`CandidatesSection`) use to edit a test's type in place (#2126).
 * Styled as the tinted intent badge it replaces (via the shared
 * `INTENT_BADGE` map), so display and edit look identical across surfaces.
 *
 * A legacy stored value (`validation` / `error-handling`, folded by #2102)
 * is run through `normalizeIntent` so the dropdown opens on the kind it now
 * IS (Negative) rather than blank; the stored value is untouched until the
 * operator actually picks something.
 *
 * `allowUnset` adds a "not specified" option and lets `onChange` receive
 * `null` — for candidates, whose intent is nullable. Planner tests always
 * carry one, so they leave it off and the dropdown offers the three kinds
 * only.
 */
const INTENTS: readonly TestIntent[] = ['happy-path', 'negative', 'edge-case']

interface Props {
  value: string | null | undefined
  onChange: (value: TestIntent | null) => void
  allowUnset?: boolean
  disabled?: boolean
  title?: string
}

export function IntentSelect({ value, onChange, allowUnset = false, disabled, title }: Props) {
  const norm = normalizeIntent(value)
  const tint = norm ? INTENT_BADGE[norm] : INTENT_NEUTRAL
  return (
    <select
      value={norm ?? ''}
      disabled={disabled}
      title={title ?? 'Test type'}
      aria-label="Test type"
      onChange={(e) => onChange(e.target.value === '' ? null : (e.target.value as TestIntent))}
      className={`px-2 py-0.5 text-xs font-medium rounded-md border cursor-pointer appearance-none outline-none disabled:cursor-not-allowed disabled:opacity-60 ${tint}`}
    >
      {allowUnset && <option value="">— Not specified —</option>}
      {INTENTS.map((i) => (
        <option key={i} value={i}>{i}</option>
      ))}
    </select>
  )
}
